import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import Header from "../../../components/Header";
import AdminService from "../../../api/services/adminService";
import "./DashboardPage.css";

const DEFAULT_PENDING = {
  deposit: 0,
  withdrawal: 0,
  support: 0,
};

const DashboardPage = () => {
  const [isSidebar, setIsSidebar] = useState(true);
  const [pendingKyc, setPendingKyc] = useState(0);
  const [pendingDeposit] = useState(DEFAULT_PENDING.deposit);
  const [pendingWithdrawal] = useState(DEFAULT_PENDING.withdrawal);
  const [pendingSupport] = useState(DEFAULT_PENDING.support);

  const [platformFeatures, setPlatformFeatures] = useState({});

  const fetchDashboardCounts = useCallback(async () => {
    const res = await AdminService.getDashboardCounts();
    if (res?.success && res.data) {
      const count = res.data.pending_kyc;
      setPendingKyc(count != null ? Number(count) : 0);
    }
  }, []);

  const fetchPlatformConfig = useCallback(async () => {
    const res = await AdminService.getPlatformConfig();
    if (res?.success && res.data) {
      const data = res.data;
      const normalized = {};
      ["spot_trading", "futures_trading", "p2p_trading", "staking", "launchpad", "swap", "deposit", "withdrawal", "full_maintenance"].forEach((key) => {
        const v = data[key];
        normalized[key] = v && typeof v === "object" ? !!v.enabled : !!v;
      });
      setPlatformFeatures(normalized);
    }
  }, []);

  useEffect(() => {
    fetchDashboardCounts();
    fetchPlatformConfig();
  }, [fetchDashboardCounts, fetchPlatformConfig]);

  useEffect(() => {
    const handler = () => fetchPlatformConfig();
    window.addEventListener("platformConfigUpdated", handler);
    return () => window.removeEventListener("platformConfigUpdated", handler);
  }, [fetchPlatformConfig]);

  const location = useLocation();
  const isInitialMount = useRef(true);

  // Close sidebar on mobile when route changes (e.g. after clicking a nav link)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const isMobile = window.innerWidth <= 992;
    if (isMobile) {
      setIsSidebar(false);
    }
  }, [location.pathname]);
  const myPermission = sessionStorage.getItem("permissions");
  const userType = sessionStorage.getItem("userType");
  const permissions = Array.isArray(JSON.parse(myPermission || "[]"))
    ? (JSON.parse(myPermission)?.map((x) => x.value) || [])
    : [];

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const actived = pathSegments[pathSegments.length - 1] || "homepage";

  const toggleSidebar = () => setIsSidebar((prev) => !prev);

  const isActive = (slug) => {
    if (!slug) return false;
    return actived === slug;
  };

  const BadgeCount = ({ count }) => {
    if (count == null || count <= 0) return null;
    return (
      <span className="sidebar-pending-badge">
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  const FeatureStatusBadge = ({ featureKey }) => {
    if (!featureKey) return null;
    const enabled = platformFeatures[featureKey];
    if (enabled == null) return null;
    const isMaintenance = featureKey === "full_maintenance";
    const label = isMaintenance ? (enabled ? "Maintenance" : "Active") : (enabled ? "On" : "Off");
    const modifier = isMaintenance ? (enabled ? "maintenance" : "active") : (enabled ? "on" : "off");
    return (
      <span className={`sidebar-feature-badge sidebar-feature-badge--${modifier}`}>
        {label}
      </span>
    );
  };

  const sectionLabel = (text, featureKey = null) => (
    <li key={`section-${text}`} className="sidebar-section-label">
      <span>{text}</span>
      {featureKey && <FeatureStatusBadge featureKey={featureKey} />}
    </li>
  );

  const navLink = (to, slug, label, icon, count = null, featureKey = null) => (
    <li key={slug || to} className={isActive(slug) ? "active" : ""}>
      <Link
        to={to}
        onClick={() => {
          if (window.innerWidth <= 992) setIsSidebar(false);
        }}
      >
        <i className={`fas ${icon}`} />
        <span>{label}</span>
        {count != null && count > 0 && <BadgeCount count={count} />}
        {featureKey && <FeatureStatusBadge featureKey={featureKey} />}
      </Link>
    </li>
  );

  const handleLogout = (e) => {
    e.preventDefault();
    sessionStorage.clear();
    window.location.href = "/";
  };

  const scrollContainerRef = useRef(null);
  const savedScrollRef = useRef(0);
  const prevPathnameRef = useRef(location.pathname);

  useLayoutEffect(() => {
    if (location.pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = location.pathname;
      savedScrollRef.current = 0;
      return;
    }
    const el = scrollContainerRef.current;
    const saved = savedScrollRef.current;
    if (el && saved > 0) {
      el.scrollTop = saved;
      const rafId = requestAnimationFrame(() => {
        el.scrollTop = saved;
      });
      return () => cancelAnimationFrame(rafId);
    }
  });

  const handleScrollSave = useCallback((e) => {
    const next = e.target.scrollTop;
    const prev = savedScrollRef.current;
    if (!(prev > 100 && next === 0)) savedScrollRef.current = next;
  }, []);

  return (
    <div className="dashboard-wrapper">
      <Header isSidebar={isSidebar} setIsSidebar={setIsSidebar} onLogout={handleLogout} />
      <div
        id="layoutSidenav"
        className={!isSidebar ? "sidebar-collapsed" : ""}
      >
        <div id="layoutSidenav_nav" className="leftside_menu sidebar-premium">
          <div className="leftside_items">
            {/* <div className="logo sidebar-logo">
              <Link
                to="/dashboard/homepage"
                onClick={() => setIsSidebar(true)}
              >
                <img
                  src="/logo192.png"
                  alt="Logo"
                  loading="lazy"
                />
              </Link>
            </div> */}
            <button
              type="button"
              className="toggle_menu"
              id="toggleBtn"
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
            >
              <i className="fas fa-bars" />
            </button>
            <div className="navi_sidebar" id="content">
              <button
                type="button"
                className="toggle_menu close_btn"
                id="closeBtn"
                onClick={toggleSidebar}
                aria-label="Close sidebar"
              >
                <i className="fas fa-times" />
              </button>
              <div className="responsive_scrool">
                <ul className="list-unstyled ps-0">
                  {sectionLabel("Main")}
                  {navLink("/dashboard/homepage", "homepage", "Dashboard", "fa-th")}
                  {navLink("/dashboard/enquiries", "enquiries", "Enquiries", "fa-envelope-open-text")}

                  {userType === "1" && sectionLabel("Platform", "full_maintenance")}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/platform-configuration",
                      "platform-configuration",
                      "Platform Configuration",
                      "fa-cog"
                    )}

                  {userType === "1" && sectionLabel("Admin")}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/listsubadmin",
                      "listsubadmin",
                      "Sub Admin Management",
                      "fa-user-friends"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/admin-logs",
                      "admin-logs",
                      "Admin Logs",
                      "fa-clipboard-list"
                    )}

                  {(permissions.includes(2) || userType === "1") &&
                    sectionLabel("Traders")}
                  {(permissions.includes(2) || userType === "1") &&
                    navLink(
                      "/dashboard/tradelist",
                      "tradelist",
                      "Traders List",
                      "fa-user-tie"
                    )}

                  {(permissions.includes(3) || userType === "1") &&
                    sectionLabel("KYC")}
                  {(permissions.includes(3) || userType === "1") &&
                    navLink(
                      "/dashboard/pendingkyc",
                      "pendingkyc",
                      "KYC Management",
                      "fa-id-card",
                      pendingKyc
                    )}

                  {/*                   {(permissions.includes(8) || userType === "1") &&
                    sectionLabel("Coin List Requests")}
                  {(permissions.includes(8) || userType === "1") &&
                    navLink(
                      "/dashboard/coinListDetails",
                      "coinListDetails",
                      "Coin Listed Details",
                      "fa-coins"
                    )}*/}
                

                  {(permissions.includes(8) || userType === "1") &&
                    sectionLabel("Spot Trading", "spot_trading")}

                      {(permissions.includes(8) || userType === "1") &&
                    navLink(
                      "/dashboard/addSpotTradingCoin",
                      "addSpotTradingCoin",
                      "Add Spot Trading Coin",
                      "fa-plus-circle"
                    )} 
                  {(permissions.includes(9) || userType === "1") &&
                    navLink(
                      "/dashboard/currencymanagement",
                      "currencymanagement",
                      "Currency Management",
                      "fa-chart-line"
                    )}
                  {(permissions.includes(10) || userType === "1") &&
                    navLink(
                      "/dashboard/addCurrencyPair",
                      "addCurrencyPair",
                      "Add Currency Pair",
                      "fa-plus-circle"
                    )}
                  {(permissions.includes(10) || userType === "1") &&
                    navLink(
                      "/dashboard/currencypair",
                      "currencypair",
                      "Currency Pair Management",
                      "fa-chart-line"
                    )}
                  {(permissions.includes(15) || userType === "1") &&
                    navLink(
                      "/dashboard/tradingfeereport",
                      "tradingfeereport",
                      "Market Trades",
                      "fa-chart-line"
                    )}
                  {(permissions.includes(16) || userType === "1") &&
                    navLink(
                      "/dashboard/OrderBook",
                      "OrderBook",
                      "OrderBook",
                      "fa-book"
                    )}
                  {(permissions.includes(17) || userType === "1") &&
                    navLink(
                      "/dashboard/allOpenOrders",
                      "allOpenOrders",
                      "Spot Open Orders",
                      "fa-list"
                    )}

                  {(permissions.includes(8) || userType === "1") &&
                    sectionLabel("Futures Trading", "futures_trading")}
                  {(permissions.includes(10) || userType === "1") &&
                    navLink(
                      "/dashboard/futuresPair",
                      "futuresPair",
                      "Futures Pair Management",
                      "fa-chart-line"
                    )}
                  {(permissions.includes(17) || userType === "1") &&
                    navLink(
                      "/dashboard/futuresOpenOrders",
                      "futuresOpenOrders",
                      "Futures Open Orders",
                      "fa-list"
                    )}
                  {(permissions.includes(17) || userType === "1") &&
                    navLink(
                      "/dashboard/futuresPositions",
                      "futuresPositions",
                      "Futures Positions",
                      "fa-bullseye"
                    )}
                  {(permissions.includes(15) || userType === "1") &&
                    navLink(
                      "/dashboard/futuresTradeHistory",
                      "futuresTradeHistory",
                      "Futures Trade History",
                      "fa-history"
                    )}

                  {(permissions.includes(14) || userType === "1") &&
                    sectionLabel("Staking", "staking")}
                  {(permissions.includes(14) || userType === "1") &&
                    navLink(
                      "/dashboard/staking-packages",
                      "staking-packages",
                      "Staking Packages",
                      "fa-coins"
                    )}
                  {(permissions.includes(14) || userType === "1") &&
                    navLink(
                      "/dashboard/staking/add-package",
                      "add-package",
                      "Add Staking Package",
                      "fa-plus-circle"
                    )}
                  {(permissions.includes(14) || userType === "1") &&
                    navLink(
                      "/dashboard/staking/user-stakes",
                      "user-stakes",
                      "User Stakes",
                      "fa-list"
                    )}

                  {(permissions.includes(11) || userType === "1") &&
                    sectionLabel("Funds", null)}
                  {(permissions.includes(11) || userType === "1") &&
                    navLink(
                      "/dashboard/fundsDManagement",
                      "fundsDManagement",
                      "Deposit",
                      "fa-arrow-down",
                      pendingDeposit,
                      "deposit"
                    )}
                  {(permissions.includes(11) || userType === "1") &&
                    navLink(
                      "/dashboard/transferTransactions",
                      "transferTransactions",
                      "Transfer Transactions",
                      "fa-exchange-alt"
                    )}
                  {(permissions.includes(12) || userType === "1") &&
                    navLink(
                      "/dashboard/fundsWithdrawalManagement",
                      "fundsWithdrawalManagement",
                      "Funds Withdrawal",
                      "fa-arrow-up",
                      pendingWithdrawal,
                      "withdrawal"
                    )}

                  {(permissions.includes(13) || userType === "1") &&
                    sectionLabel("Exchange")}
                  {(permissions.includes(13) || userType === "1") &&
                    navLink(
                      "/dashboard/TradingCommision",
                      "TradingCommision",
                      "Trading Commission",
                      "fa-chart-pie"
                    )}
                  {(permissions.includes(13) || userType === "1") &&
                    navLink(
                      "/dashboard/WithdrawalFees",
                      "WithdrawalFees",
                      "Withdrawal Fees",
                      "fa-percent"
                    )}
                  {(permissions.includes(14) || userType === "1") &&
                    navLink(
                      "/dashboard/exchangeWalletManagement",
                      "exchangeWalletManagement",
                      "Exchange Wallet",
                      "fa-wallet"
                    )}
                  {(permissions.includes(14) || userType === "1") &&
                    navLink(
                      "/dashboard/LaunchpadManagement",
                      "LaunchpadManagement",
                      "Launchpad Management",
                      "fa-rocket",
                      null,
                      "launchpad"
                    )}
                  {(permissions.includes(14) || userType === "1") &&
                    navLink(
                      "/dashboard/AdminDebitCreditTrans",
                      "AdminDebitCreditTrans",
                      "Admin Debit Credit",
                      "fa-exchange-alt"
                    )}
                  {(permissions.includes(14) || userType === "1") &&
                    navLink(
                      "/dashboard/AnnouncementDetails",
                      "AnnouncementDetails",
                      "Announcement",
                      "fa-bullhorn"
                    )}

                  {userType === "1" && sectionLabel("P2P", "p2p_trading")}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-orders",
                      "p2p-orders",
                      "P2P Orders",
                      "fa-exchange-alt"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-ads",
                      "p2p-ads",
                      "P2P Ads",
                      "fa-ad"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/dispute_management",
                      "dispute_management",
                      "Dispute Management",
                      "fa-gavel"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-fiat-management",
                      "p2p-fiat-management",
                      "Fiat Currency",
                      "fa-money-bill"
                    )}
                  {userType === "1" &&
                    navLink(
                      "/dashboard/p2p-user-payment-methods",
                      "p2p-user-payment-methods",
                      "User Payment Methods",
                      "fa-credit-card"
                    )}

                  {(permissions.includes(18) ||
                    permissions.includes(20) ||
                    userType === "1") &&
                    sectionLabel("System")}
                  {(permissions.includes(18) || userType === "1") &&
                    navLink(
                      "/dashboard/notification",
                      "notification",
                      "Notifications",
                      "fa-bell"
                    )}
                  {(permissions.includes(18) || userType === "1") &&
                    navLink(
                      "/dashboard/send-email",
                      "send-email",
                      "Send Email",
                      "fa-envelope"
                    )}
                  {(permissions.includes(20) || userType === "1") &&
                    navLink(
                      "/dashboard/support",
                      "support",
                      "Support",
                      "fa-headset",
                      pendingSupport
                    )}
                  {(permissions.includes(20) || userType === "1") &&
                    navLink(
                      "/dashboard/UpdateApk",
                      "UpdateApk",
                      "Update Apk",
                      "fa-mobile-alt"
                    )}
                </ul>
              </div>
              <div className="logout_btn">
                <a href="#/" onClick={handleLogout}>
                  <i className="fas fa-sign-out-alt" />
                  <span>Logout</span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <div
          ref={scrollContainerRef}
          className="layoutSidenav_content"
          onScroll={handleScrollSave}
        >
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
